import { fabric } from 'fabric';
import CollaborationBehaviors from './CollaborationBehaviors';
import AnalyticsBehaviors from './AnalyticsBehaviors';
import FrameMemberBehaviors from './FrameMemberBehaviors';
import FrameBehaviors from './FrameBehaviors';
import type WhiteboardController from '../controllers/WhiteboardController';
import {
  type IAnimationOptions,
  type IObjectOptions,
} from 'fabric/fabric-impl';
import LayoutBehaviors from './LayoutBehaviors';
import { type RecordId } from '@aha-app/aha-editor-v2';
import SmartGuideBehaviors from './SmartGuideBehaviors';
import {
  isMindMap,
  isMindMapBranch,
  isMindMapLeaf,
  isSimpleSelection,
} from './guards';
import type MindMap from './MindMap';
import { type MindMapSides, type ParentId } from './MindMap';
import type MindMapLeaf from './MindMapLeaf';

export type MindMapBranchOptions = IObjectOptions & {
  id?: RecordId;
  controller: WhiteboardController;
  side: MindMapSides;
  layoutDirection?: string;
  parentId?: ParentId;
  mindMapId?: RecordId;
  layoutMinWidth?: number;
  layoutMinHeight?: number;
};

// Frame-like object that contains all the children of a parent object in a MindMap
interface MindMapBranch
  extends fabric.Object,
    SmartGuideBehaviors,
    CollaborationBehaviors,
    LayoutBehaviors,
    FrameBehaviors,
    FrameMemberBehaviors {
  side: MindMapSides;
  parentId?: ParentId;
  mindMapId: RecordId;
}

@AnalyticsBehaviors
@LayoutBehaviors
@FrameBehaviors
@FrameMemberBehaviors
@SmartGuideBehaviors
@CollaborationBehaviors
class MindMapBranch extends fabric.Object {
  type = 'mindMapBranch';

  notInNavigation = true;
  layoutEnabled = true;
  layoutGap = 64;
  layoutPadding = 0;
  layoutSizeVertical: 'children' = 'children';
  layoutSizeHorizontal: 'children' = 'children';
  layoutMinWidth = 128;
  layoutMinHeight = 48;
  evented = false;
  selectable = false;
  fill = 'transparent';
  // Where the animation will end, used for layout calculations during animation
  animationTargets: Partial<this> = {};

  serializedKeys: Array<string | RegExp> = [
    'z',
    'left',
    'top',
    'width',
    'height',
    'container',
    'side',
    'parentId',
    'mindMapId',
  ];

  constructor(options: MindMapBranchOptions) {
    super(options);
    if (options.layoutMinWidth != null) {
      this.layoutMinWidth = options.layoutMinWidth;
    }
    if (options.layoutMinHeight != null) {
      this.layoutMinHeight = options.layoutMinHeight;
    }
    const isRemote = this.controller.client.processingRemoteOps;
    const isLoaded = this.controller.state.isLoaded;

    this.initializeFrameMember();
    this.enableFrameBehaviors();
    this.initializeLayout();
    this.updateLayoutAlign();

    if (isRemote && isLoaded) {
      setTimeout(() => this.initializeRemoteCreate(), 1);
    } else {
      this.layout();
    }
  }

  containedChildren(): fabric.Object[] {
    // Return nothing to improve peformance, our children will automatically be moved with us anyway
    return [];
  }

  initializeRemoteCreate() {
    // Created via remote op, notify the MindMap so it can update the DiagramDatabase
    const mindmap = this.controller.dataStoreObjects.findRecordById(
      this.mindMapId
    );
    if (!isMindMap(mindmap)) {
      throw new Error(
        mindmap
          ? `MindMap ${this.mindMapId} is not actually a MindMap`
          : 'MindMap is missing'
      );
    }
    mindmap.insertBranch(this);
  }

  updateLayoutAlign() {
    switch (this.side) {
      case 'west':
        this.set('layoutAlignHorizontal', 'right');
        break;
      case 'east':
        this.set('layoutAlignHorizontal', 'left');
        break;

      case 'north':
      case 'south':
        this.set('layoutAlignHorizontal', 'center');
        break;
    }
  }

  // Prevent branches being absorbed into other frames
  canAddToFrame(mindmap?: FrameBehaviors & fabric.Object) {
    return (
      isMindMap(mindmap) && (!this.mindMapId || mindmap.id === this.mindMapId)
    );
  }

  canAddFrameMember(obj: FrameMemberBehaviors & fabric.Object): boolean {
    const prevBranch = obj.inFrame();

    if (isSimpleSelection(obj)) {
      // Multiple objects selected, do some special stuff

      // Only allow if entire selection are from the same branch
      let branch: MindMapBranch | undefined;
      for (const leaf of obj._objects) {
        if (!isMindMapLeaf(leaf)) return false;
        if (!branch) branch = leaf.getBranch();
        if (!branch) return false;
        if (branch?.id !== leaf.getBranch()?.id) return false;
      }
      return true;
    }

    // Don't allow anything created outside of this specific MindMap
    else if (
      !isMindMapBranch(prevBranch) ||
      (prevBranch.mindMapId && prevBranch.mindMapId !== this.mindMapId)
    ) {
      return false;
    }

    return true;
  }

  canRemoveFrameMember(): boolean {
    // Once you join us, you'll never leave
    return false;
  }

  // How deep is this branch in the hierarchy
  depth() {
    if (!this.parentId) return 0;
    let depth = 1;
    let current: MindMapBranch | undefined = this;

    while (current?.parentId && depth < 100) {
      const parent = this.controller.dataStoreObjects.findRecordById(
        current.parentId as RecordId
      );
      if (!isMindMapLeaf(parent)) {
        break;
      }
      const branch = parent.getBranch();
      current = branch;
      depth += 1;
    }
    return depth;
  }

  get leaves() {
    return this.sortedChildren().filter(isMindMapLeaf);
  }

  getMindMap(): MindMap | undefined {
    if (!this.canvas) return;
    return this.canvas.findFirstObject(
      (o): o is MindMap => isMindMap(o) && o.id === this.mindMapId
    );
  }

  getParentLeaf(): MindMapLeaf | undefined {
    return this.canvas.findFirstObject(
      (o): o is MindMapLeaf => o.id === this.parentId
    );
  }

  /**
   * Get the first object (MindMapLeaf) in the hierarchy.
   * i.e. the one closest to the center on the same side as this branch
   */
  getRootObject(): MindMapLeaf | undefined {
    let parent = this.getParentLeaf();
    if (!parent) return;
    let parentBranch = parent.inFrame()! as MindMapBranch | undefined;
    const mindmap = parentBranch?.getMindMap();
    if (!mindmap) return;
    while (
      isMindMapBranch(parentBranch) &&
      parentBranch.parentId &&
      !parentBranch.parentId.startsWith('root-')
    ) {
      const parentId = parentBranch.parentId as RecordId;
      parentBranch = mindmap.getBranchContainingId(parentId);
      parent = parentBranch?.leaves.find(o => o.id === parentId);
    }

    return parent;
  }

  animate(
    propertyOrProperties: string | { [key: string]: number | string },
    valueOrOptions?: number | string | IAnimationOptions,
    options?: IAnimationOptions
  ): fabric.Object {
    // Overriding animate() method so we can disable collaboration while it's animating
    this.collaborate = false;

    const onComplete = () => {
      setTimeout(() => (this.collaborate = true), 1);
    };

    if (typeof propertyOrProperties === 'string') {
      this.animationTargets[propertyOrProperties] = valueOrOptions;
      const modifiedOptions = {
        ...options,
        onComplete: () => {
          delete this.animationTargets[propertyOrProperties];
          onComplete();
          options?.onComplete?.();
        },
      };
      return super.animate(
        propertyOrProperties,
        valueOrOptions as number | string,
        modifiedOptions
      );
    } else {
      Object.assign(this.animationTargets, propertyOrProperties);
      const animationOptions = valueOrOptions as IAnimationOptions;
      const modifiedOptions = {
        ...animationOptions,
        onComplete: () => {
          for (const key in propertyOrProperties) {
            delete this.animationTargets[key];
          }
          onComplete();
          animationOptions?.onComplete?.();
        },
      };
      return super.animate(propertyOrProperties, modifiedOptions);
    }
  }

  _render(ctx: CanvasRenderingContext2D) {
    super._render(ctx);
    const { width, height } = this;
    // ctx.fillStyle = '#000';
    // ctx.fillText(`ID: ${this.id}`, -width/2, 0);
    ctx.fillStyle = this.fill ?? 'transparent';
    ctx.fillRect(-width / 2, -height / 2, width, height);
  }
}

export { MindMapBranch as default };
